import { useEffect, useRef } from "react";

/**
 * Dispatched by native (iOS/Android) into the Care WebView when the Care tab
 * becomes visible or is reselected:
 *
 *   window.dispatchEvent(new CustomEvent("noom:webviewShown"))
 *
 * Keep this string in sync with the native inject snippets.
 */
export const WEBVIEW_SHOWN_EVENT = "noom:webviewShown";

/** Coalesce native + visibilitychange + pageshow that fire on the same focus. */
const MIN_REFRESH_INTERVAL_MS = 1500;

type UseCareTabFocusRefreshOptions = {
  minIntervalMs?: number;
};

/**
 * Calls `refresh` when the Care tab regains focus. Primary signal is the
 * native-injected `noom:webviewShown` event; `visibilitychange` (visible)
 * and `pageshow` are browser/WebView fallbacks.
 *
 * Concurrent signals are coalesced via an in-flight guard and a short min
 * interval so a single tab return does not triple-hit GraphQL.
 */
export const useCareTabFocusRefresh = (
  refresh: () => void,
  options?: UseCareTabFocusRefreshOptions
): void => {
  const minIntervalMs = options?.minIntervalMs ?? MIN_REFRESH_INTERVAL_MS;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  // The queries already fetch on mount. Treat mounting as the first refresh
  // so initial WebView focus/pageshow signals do not immediately duplicate it.
  const lastRefreshAtRef = useRef(Date.now());
  const inFlightRef = useRef(false);

  useEffect(() => {
    const onFocus = () => {
      const now = Date.now();
      if (inFlightRef.current) {
        return;
      }
      if (now - lastRefreshAtRef.current < minIntervalMs) {
        return;
      }

      inFlightRef.current = true;
      lastRefreshAtRef.current = now;
      try {
        refreshRef.current();
      } finally {
        // refresh() is fire-and-forget today (void refetch). Clear in-flight on
        // the next macrotask so a synchronous double-dispatch is dropped, while
        // a later genuine focus (after minIntervalMs) can still run.
        window.setTimeout(() => {
          inFlightRef.current = false;
        }, 0);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        onFocus();
      }
    };

    const onPageShow = (event: { persisted?: boolean }) => {
      // A normal initial page load also emits pageshow. Only bfcache restores
      // represent a page regaining focus and require a refresh.
      if (event.persisted) {
        onFocus();
      }
    };

    window.addEventListener(WEBVIEW_SHOWN_EVENT, onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener(WEBVIEW_SHOWN_EVENT, onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [minIntervalMs]);
};
